// CRITICAL: Import error suppression FIRST before React
import './setupErrorSuppression';

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { initConsoleFilter } from './utils/console-filter';
import ErrorBoundary from './components/ErrorBoundary';

// AGGRESSIVE EZOPEN ERROR SUPPRESSION - Must run before React error overlay
// This prevents EZOPEN SDK errors from reaching the React error overlay
(function() {
  // Store original error handler
  const originalErrorHandler = window.onerror;
  const originalRejectionHandler = window.onunhandledrejection;
  
  // Override window.onerror with EZOPEN filter
  window.onerror = function(message: any, source?: string, lineno?: number, colno?: number, error?: Error) {
    const msg = String(message || '');
    const src = String(source || '');
    const stack = error?.stack || '';
    
    // Suppress ALL EZOPEN SDK errors completely
    if (msg.includes('wasmplayer') || 
        msg.includes('jsPlugin') || 
        msg.includes('ezopen') ||
        msg.includes("reading 'width'") ||
        msg.includes('Cannot read properties of null') ||
        msg.includes('Unknown') ||
        src.includes('wasmplayer') ||
        src.includes('jsPlugin') ||
        src.includes('ezopen') ||
        stack.includes('wasmplayer') ||
        stack.includes('jsPlugin') ||
        stack.includes('ezopen')) {
      return true; // Suppress - prevents default error handling
    }
    
    // Call original handler for other errors
    if (originalErrorHandler) {
      return originalErrorHandler.call(window, message, source, lineno, colno, error);
    }
    return false;
  };
  
  // Override unhandledrejection with EZOPEN filter
  window.onunhandledrejection = function(event: PromiseRejectionEvent) {
    const reason = event.reason;
    
    // Always suppress undefined/null rejections (EZOPEN throws these)
    if (reason === undefined || reason === null) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    
    const msg = typeof reason === 'string' ? reason : 
                reason instanceof Error ? reason.message : 
                String(reason);
    const stack = reason instanceof Error ? (reason.stack || '') : '';
    
    // Suppress EZOPEN SDK promise rejections
    if (msg.includes('wasmplayer') || 
        msg.includes('jsPlugin') || 
        msg.includes('ezopen') ||
        msg.includes("reading 'width'") ||
        msg.includes('Cannot read properties of null') ||
        stack.includes('wasmplayer') ||
        stack.includes('jsPlugin') ||
        stack.includes('ezopen')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    
    // Call original handler for other rejections
    if (originalRejectionHandler) {
      originalRejectionHandler.call(window, event);
    }
  };
  
  // Prevent React error overlay from showing EZOPEN errors
  window.addEventListener('error', function(event) {
    const msg = event.message || event.error?.message || '';
    const src = event.filename || '';
    const stack = event.error?.stack || '';
    
    if (msg.includes('wasmplayer') || 
        msg.includes('jsPlugin') || 
        msg.includes('ezopen') ||
        msg.includes("reading 'width'") ||
        msg.includes('Cannot read properties of null') ||
        src.includes('wasmplayer') ||
        src.includes('jsPlugin') ||
        stack.includes('wasmplayer') ||
        stack.includes('jsPlugin')) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }, true); // Use capture phase to catch before React
  
  window.addEventListener('unhandledrejection', function(event) {
    const reason = event.reason;
    
    if (reason === undefined || reason === null) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    
    const msg = typeof reason === 'string' ? reason : 
                reason instanceof Error ? reason.message : 
                String(reason);
    const stack = reason instanceof Error ? (reason.stack || '') : '';
    
    if (msg.includes('wasmplayer') || 
        msg.includes('jsPlugin') || 
        msg.includes('ezopen') ||
        msg.includes("reading 'width'") ||
        msg.includes('Cannot read properties of null') ||
        stack.includes('wasmplayer') ||
        stack.includes('jsPlugin')) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }, true); // Use capture phase
})();

// Initialize console filter to suppress noisy third-party SDK logs
initConsoleFilter();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
